import { FormEvent, useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { HomeLanding, type Persona } from './homeLanding';
import { useAuth } from '../auth/AuthContext';
import { ApiError } from '../api/client';
import { PublicTrackingInfo, trackPublicOrder } from '../api/orders';
import {
  DelivererRow,
  fetchDeliverersNearLocation,
  searchDeliverers
} from '../api/deliverers';
import { StatusBadge } from '../components/StatusBadge';
import { Spinner } from '../components/ui/Spinner';
import { formatDateTime } from '../lib/format';
import { Avatar } from '../components/Avatar';

type DeliveryScope = 'city' | 'district' | 'region' | 'country' | 'international';

const SCOPE_LABELS: Record<DeliveryScope, string> = {
  city: 'Local (ville)',
  district: 'Régional (district)',
  region: 'Régional (région)',
  country: 'National',
  international: 'International (monde entier)'
};

const VEHICLE_LABELS: Record<string, string> = {
  bike: 'Vélo',
  motorbike: 'Moto',
  car: 'Voiture',
  van: 'Camionnette',
  small_truck: 'Petit camion',
  medium_truck: 'Camion moyen',
  large_truck: 'Grand camion',
  refrigerated: 'Réfrigéré',
  other: 'Autre'
};

const formatVehicle = (t: string) => VEHICLE_LABELS[t] ?? t;

// ============================================================
// SECTION VENDEUR — recherche de livreur
// ============================================================

const SenderSection = ({ initialVehicle = '' }: { initialVehicle?: string }) => {
  const [mode, setMode] = useState<'geo' | 'criteria'>('criteria');
  const [vehicleType, setVehicleType] = useState<string>(initialVehicle);
  const [baseCity, setBaseCity] = useState<string>('');
  const [deliveryScope, setDeliveryScope] = useState<DeliveryScope | ''>('');
  const [radius, setRadius] = useState<number>(10);
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [geoError, setGeoError] = useState<string | null>(null);

  useEffect(() => {
    setVehicleType(initialVehicle);
    if (initialVehicle) setMode('criteria');
  }, [initialVehicle]);

  const search = useMutation({
    mutationFn: async () => {
      if (mode === 'geo') {
        if (!coords) throw new Error('Position GPS requise');
        return fetchDeliverersNearLocation({ lat: coords.lat, lng: coords.lng, radius });
      }
      return searchDeliverers({
        vehicleType: vehicleType || undefined,
        baseCity: baseCity || undefined,
        deliveryScope: deliveryScope || undefined
      });
    }
  });

  const requestGeolocation = () => {
    setGeoError(null);
    if (!navigator.geolocation) {
      setGeoError('Géolocalisation indisponible sur ce navigateur');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => setGeoError(err.message),
      { timeout: 10_000 }
    );
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    search.mutate();
  };

  return (
    <div className="space-y-6">
      <div className="flex gap-2">
        <button
          type="button"
          className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-all ${
            mode === 'criteria'
              ? 'border-brand-500 bg-brand-50 text-brand-700'
              : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
          }`}
          onClick={() => setMode('criteria')}
        >
          Par critères
        </button>
        <button
          type="button"
          className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-all ${
            mode === 'geo'
              ? 'border-brand-500 bg-brand-50 text-brand-700'
              : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
          }`}
          onClick={() => setMode('geo')}
        >
          Près de moi (GPS)
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {mode === 'criteria' && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Type de véhicule</span>
              <select
                className="input mt-1.5"
                value={vehicleType}
                onChange={(e) => setVehicleType(e.target.value)}
              >
                <option value="">Tous</option>
                <option value="bike">Vélo</option>
                <option value="motorbike">Moto</option>
                <option value="car">Voiture</option>
                <option value="van">Camionnette</option>
                <option value="small_truck">Petit camion</option>
                <option value="refrigerated">Réfrigéré</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Ville (optionnel)</span>
              <input
                className="input mt-1.5"
                placeholder="Libreville, Lagos, Casablanca, Paris…"
                value={baseCity}
                onChange={(e) => setBaseCity(e.target.value)}
              />
            </label>
            <label className="block sm:col-span-2">
              <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Rayon d'action souhaité
              </span>
              <select
                className="input mt-1.5"
                value={deliveryScope}
                onChange={(e) => setDeliveryScope(e.target.value as DeliveryScope | '')}
              >
                <option value="">Tous rayons</option>
                <option value="city">{SCOPE_LABELS.city}</option>
                <option value="district">{SCOPE_LABELS.district}</option>
                <option value="region">{SCOPE_LABELS.region}</option>
                <option value="country">{SCOPE_LABELS.country}</option>
                <option value="international">{SCOPE_LABELS.international}</option>
              </select>
              <p className="mt-1 text-[11px] text-slate-500">
                Choisissez « International » pour les expéditions hors frontières.
              </p>
            </label>
          </div>
        )}

        {mode === 'geo' && (
          <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4">
            {!coords && (
              <button type="button" className="btn-ghost" onClick={requestGeolocation}>
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 22s-8-7.58-8-13a8 8 0 1116 0c0 5.42-8 13-8 13z" />
                  <circle cx="12" cy="9" r="2.5" />
                </svg>
                Activer ma localisation
              </button>
            )}
            {coords && (
              <p className="text-xs text-slate-600">
                📍 Position : {coords.lat.toFixed(4)}, {coords.lng.toFixed(4)}{' '}
                <button
                  type="button"
                  className="ml-2 text-brand-600 hover:underline"
                  onClick={() => setCoords(null)}
                >
                  Modifier
                </button>
              </p>
            )}
            {geoError && <p className="mt-2 text-xs text-red-600">{geoError}</p>}
            {coords && (
              <label className="mt-3 block">
                <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Rayon (km)</span>
                <input
                  type="number"
                  min={1}
                  max={100}
                  className="input mt-1.5 max-w-[120px]"
                  value={radius}
                  onChange={(e) => setRadius(Number(e.target.value))}
                />
              </label>
            )}
          </div>
        )}

        <button type="submit" className="btn-primary w-full" disabled={search.isPending}>
          {search.isPending ? (
            <>
              <Spinner className="h-4 w-4 border-2 border-white border-t-transparent" />
              Recherche…
            </>
          ) : (
            'Trouver des livreurs disponibles'
          )}
        </button>
      </form>

      {search.error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {search.error instanceof ApiError ? search.error.message : (search.error as Error).message}
        </div>
      )}

      {search.data && <DelivererResults rows={search.data.data} />}
    </div>
  );
};

const DelivererResults = ({ rows }: { rows: DelivererRow[] }) => {
  if (rows.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50/40 p-6 text-center text-sm text-slate-500">
        Aucun livreur trouvé avec ces critères. Élargissez le rayon ou retirez un filtre.
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
        {rows.length} livreur{rows.length > 1 ? 's' : ''} disponible{rows.length > 1 ? 's' : ''}
      </p>
      {rows.slice(0, 10).map((d) => (
        <div
          key={d.id}
          className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 bg-white p-4 transition-shadow hover:shadow-md"
        >
          <div className="flex min-w-0 items-center gap-3">
            <Avatar
              size="md"
              profilePictureUrl={d.User?.profilePictureUrl}
              firstName={d.User?.firstName}
              lastName={d.User?.lastName}
            />
            <div className="min-w-0">
            <p className="font-semibold text-slate-900 truncate">
              {d.companyName || (d.User ? `${d.User.firstName} ${d.User.lastName}` : 'Livreur')}
            </p>
            <p className="mt-0.5 text-xs text-slate-500">
              {formatVehicle(d.vehicleType)}
              {d.pricePerKm != null && (
                <span className="ml-2">· {d.pricePerKm.toFixed(2)} {d.currency}/km</span>
              )}
              {d.rating != null && d.rating > 0 && (
                <span className="ml-2">· ⭐ {d.rating.toFixed(1)}</span>
              )}
            </p>
            </div>
          </div>
          <Link
            to={`/register?role=client&deliverer=${d.id}`}
            className="btn-primary text-xs"
          >
            Commander
          </Link>
        </div>
      ))}
      {rows.length > 10 && (
        <p className="text-center text-xs text-slate-500">
          {rows.length - 10} autres livreurs disponibles — créez un compte pour voir la liste complète.
        </p>
      )}
    </div>
  );
};

// ============================================================
// SECTION LIVREUR — onboarding rapide
// ============================================================

const DelivererSignupSection = () => {
  const benefits = [
    'Le prix de la course vous revient en entier',
    'Vous choisissez quand vous êtes disponible',
    'Tout se fait sur votre téléphone',
    'Les boutons disent quoi faire, sans jargon'
  ];
  return (
    <div className="space-y-6">
      <div className="rounded-xl bg-gradient-to-br from-brand-50 to-slate-50 p-5">
        <p className="text-sm font-semibold text-slate-900">Pourquoi devenir livreur Nafola ?</p>
        <ul className="mt-3 space-y-2">
          {benefits.map((b) => (
            <li key={b} className="flex items-start gap-2 text-sm text-slate-700">
              <svg className="mt-0.5 h-4 w-4 flex-none text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <p className="text-sm font-semibold text-slate-900">3 étapes simples</p>
        <ol className="mt-3 space-y-3">
          <li className="flex gap-3 text-sm">
            <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-brand-500 text-xs font-bold text-white">1</span>
            <span className="text-slate-700">Créez un compte livreur, avec votre nom et votre téléphone</span>
          </li>
          <li className="flex gap-3 text-sm">
            <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-brand-500 text-xs font-bold text-white">2</span>
            <span className="text-slate-700">Dites si vous êtes à vélo, en moto ou en voiture</span>
          </li>
          <li className="flex gap-3 text-sm">
            <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-brand-500 text-xs font-bold text-white">3</span>
            <span className="text-slate-700">Dites que vous êtes disponible, puis prenez une course</span>
          </li>
        </ol>
      </div>

      <Link to="/register?role=deliverer" className="btn-primary w-full">
        Devenir livreur — Inscription rapide
      </Link>
      <p className="text-center text-xs text-slate-500">
        Déjà inscrit ?{' '}
        <Link to="/login" className="text-brand-600 hover:underline">
          Se connecter
        </Link>
      </p>
    </div>
  );
};

// ============================================================
// SECTION CLIENT — tracking public par code
// ============================================================

const TRACKING_CODE_PATTERN = /^(NF|BW)-[A-Z0-9-]+$/i;

const TrackingSection = () => {
  const [code, setCode] = useState('');
  const [info, setInfo] = useState<PublicTrackingInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  const track = useMutation({
    mutationFn: (c: string) => trackPublicOrder(c),
    onSuccess: (res) => {
      setInfo(res.data);
      setError(null);
    },
    onError: (err) => {
      setInfo(null);
      setError(err instanceof ApiError ? err.message : 'Erreur de suivi');
    }
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return;
    if (!TRACKING_CODE_PATTERN.test(trimmed)) {
      setError('Format de code attendu : NF-XXXX-XXXXXX');
      setInfo(null);
      return;
    }
    track.mutate(trimmed);
  };

  return (
    <div className="space-y-5">
      <form onSubmit={handleSubmit} className="space-y-3">
        <label className="block">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Code de suivi</span>
          <input
            className="input mt-1.5 font-mono uppercase"
            placeholder="NF-XXXX-XXXXXX"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoCapitalize="characters"
            spellCheck={false}
          />
        </label>
        <button type="submit" className="btn-primary w-full" disabled={track.isPending}>
          {track.isPending ? (
            <>
              <Spinner className="h-4 w-4 border-2 border-white border-t-transparent" />
              Recherche…
            </>
          ) : (
            'Suivre ma commande'
          )}
        </button>
      </form>

      <p className="text-xs text-slate-500">
        Le code de suivi est fourni par votre vendeur ou affiché lors de votre commande.
      </p>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      )}

      {info && (
        <div className="rounded-xl border border-slate-200 bg-white p-5 animate-fade-in">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Code</p>
              <p className="mt-1 font-mono text-base font-medium text-slate-900">{info.trackingCode}</p>
            </div>
            <StatusBadge status={info.status} />
          </div>
          <div className="mt-4 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Prise en charge</p>
              <p className="mt-1 leading-snug text-slate-800">{info.pickupAddress}</p>
              {info.actualPickupTime && (
                <p className="mt-1 text-xs text-slate-500">Effective : {formatDateTime(info.actualPickupTime)}</p>
              )}
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Livraison</p>
              <p className="mt-1 leading-snug text-slate-800">{info.deliveryAddress}</p>
              {info.actualDeliveryTime ? (
                <p className="mt-1 text-xs text-emerald-700">✓ Livré : {formatDateTime(info.actualDeliveryTime)}</p>
              ) : info.estimatedDeliveryTime ? (
                <p className="mt-1 text-xs text-slate-500">Estimée : {formatDateTime(info.estimatedDeliveryTime)}</p>
              ) : null}
            </div>
          </div>
          <div className="mt-4 border-t border-slate-100 pt-3 text-xs text-slate-500">
            Destinataire : <span className="font-medium text-slate-700">{info.recipientName}</span>
            <span className="mx-2">·</span>
            Expéditeur : <span className="font-medium text-slate-700">{info.senderName}</span>
            <span className="mx-2">·</span>
            Distance : {info.distance.toFixed(1)} km
          </div>
          <p className="mt-3 text-xs text-slate-500">
            Pour voir la course avancer toute seule, connectez-vous à votre compte.{' '}
            <Link to="/login" className="text-brand-600 hover:underline">
              Se connecter
            </Link>
          </p>
        </div>
      )}
    </div>
  );
};

export const HomePage = () => {
  const { user } = useAuth();
  const [persona, setPersona] = useState<Persona>('sender');
  const [vehiclePreset, setVehiclePreset] = useState('');

  const openPersona = (key: Persona) => {
    setPersona(key);
    document.getElementById('persona-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <HomeLanding
      firstName={user?.firstName}
      role={user?.role}
      persona={persona}
      onOpen={openPersona}
      onVehicle={(id) => {
        setVehiclePreset(id);
        openPersona('sender');
      }}
      panel={
        <>
          <h2 className="text-lg font-semibold text-slate-900">
            {persona === 'sender' && 'Trouver un livreur disponible'}
            {persona === 'deliverer' && 'Devenir livreur'}
            {persona === 'tracking' && 'Suivre une course'}
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            {persona === 'sender' && 'Choisissez un véhicule, une ville, ou votre position.'}
            {persona === 'deliverer' && 'Un compte, un véhicule, et vous pouvez prendre une course.'}
            {persona === 'tracking' && 'Le code commence par NF-. Il est sur la course.'}
          </p>
          <div className="mt-6">
            {persona === 'sender' && <SenderSection initialVehicle={vehiclePreset} />}
            {persona === 'deliverer' && <DelivererSignupSection />}
            {persona === 'tracking' && <TrackingSection />}
          </div>
        </>
      }
    />
  );
};
